import React, { Fragment } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { applyMiddleware, createStore } from 'redux';
import { composeWithDevTools } from '@redux-devtools/extension';
import { thunk } from 'redux-thunk';
import app, { createReducer } from './reducers';
import { historyMiddleware } from './Middlewares/History.middleware';
import LoaderContainer from './Containers/Loader.container';
import ModalResultContainer from './Containers/ModalResult.container';
import LiveEditing from './Components/LiveEditing';
import MiniCart from './Components/MiniCart';
import ShoppingCart from './Components/ShoppingCart';
import QuickSearch from './Components/QuickSearch';
import NavigationContainer from './Containers/Navigation.container';
import FacetedSearch from './Components/FacetedSearch';
import FacetedSearchCompactContainer from './Components/FacetedSearchCompactContainer';
import DynamicComponent from './Components/DynamicComponent';
import VanillaComponents from './VanillaComponents/index';

window.__litium = window.__litium || {};
const preloadState = window.__litium.preloadState || {};
const store = createStore(app, preloadState, composeWithDevTools(applyMiddleware(thunk, historyMiddleware)));

// Add a dictionary to keep track of the registered async reducers
store.asyncReducers = {};

// Create an inject reducer function
// This function adds the async reducer, and creates a new combined reducer
store.injectReducer = (key, asyncReducer) => {
    if (!store.asyncReducers[key]) {
        store.asyncReducers[key] = asyncReducer;
        store.replaceReducer(createReducer(store.asyncReducers));
    }
};

window.__litium = {
    ...window.__litium,
    bootstrapComponents: () => {
        // bootstrap React components, in case the HTML response we receive from the server
        // has React components. ReactDOM.render performs only an update on previous rendered
        // components and only mutate the DOM as necessary to reflect latest React element.
        bootstrapComponents();
    },
    cache: {}, // for storing cache data for current request
};

// Checks if the element is a react element
const isReactRendered = (node) => Object.keys(node).filter((key) => key.includes('_react')).length > 0;

// If element already is a React component don't rerender it
const shouldRender = (element) => element && !isReactRendered(element);

const registeredContainers = [];
const renderReact = (element, container) => {
    if (shouldRender(container)) {
        const containerId = container?.className ?? container?.id;
        const containerRoot = createRoot(container, containerId);
        registeredContainers.push(containerRoot);

        containerRoot.render(element);
    }
};

window.onunload = () => {
    // make sure components are unmounted, redux's listener are unsubscribed
    // to fix issue with iframe navigation in IE
    registeredContainers.forEach((container) => {
        container.unmount();
    });
};

const loaderComponent = () => {
    if (document.getElementById('globalLoader')) {
        renderReact(
            <Provider store={store}>
                <LoaderContainer />
            </Provider>,
            document.getElementById('globalLoader')
        );
    }
};

const modalResultComponent = () => {
    if (document.getElementById('globalModalResult')) {
        renderReact(
            <Provider store={store}>
                <ModalResultContainer />
            </Provider>,
            document.getElementById('globalModalResult')
        );
    }
};

const bootstrapComponents = () => {
    if (document.getElementById('liveEditing')) {
        renderReact(
            <Provider store={store}>
            <LiveEditing />
            </Provider>,
            document.getElementById('liveEditing')
       );
    }

    if (document.getElementById('miniCart')) {
        renderReact(
            <Provider store={store}>
                <MiniCart />
            </Provider>,
            document.getElementById('miniCart')
        );
    }

    if (document.getElementById('shoppingCart')) {
        renderReact(
            <Provider store={store}>
                <ShoppingCart />
            </Provider>,
            document.getElementById('shoppingCart')
        );
    }

    if (document.getElementById('quickSearch')) {
        const isLoggedIn =
            document.getElementById('quickSearch').dataset.isLoggedIn.toLowerCase() === 'true' ? true : false;

        renderReact(
            <Provider store={store}>
                <QuickSearch isLoggedIn={isLoggedIn} />
            </Provider>,
            document.getElementById('quickSearch')
        );
    }
    if (document.querySelectorAll('.navbar')) {
        Array.from(document.querySelectorAll('.navbar')).forEach((navigation) => {
            renderReact(
                <Provider store={store}>
                    <NavigationContainer />
                </Provider>,
                navigation
            );
        });
    }
    if (document.getElementById('facetedSearch')) {
        renderReact(
            <Provider store={store}>
                <FacetedSearch />
            </Provider>,
            document.getElementById('facetedSearch')
        );
    }
    if (document.getElementById('facetedSearchCompact')) {
        const rowsPerPage = document.getElementById('facetedSearchCompact').dataset.rowsPerPage;
        renderReact(
            <Provider store={store}>
                <FacetedSearchCompactContainer rowsPerPage={JSON.parse(rowsPerPage)} />
            </Provider>,
            document.getElementById('facetedSearchCompact')
        );
    }
    if (document.getElementById('myPagePersons')) {
        const PersonList = DynamicComponent({
            loader: () => import('./Components/PersonListContainer'),
        });
        renderReact(
            <Provider store={store}>
                <PersonList />
            </Provider>,
            document.getElementById('myPagePersons')
        );
    }
    if (document.getElementById('myPageAddresses')) {
        const AddressList = DynamicComponent({
            loader: () => import('./Components/AddressListContainer'),
        });
        renderReact(
            <Provider store={store}>
                <AddressList />
            </Provider>,
            document.getElementById('myPageAddresses')
        );
    }
    if (document.getElementById('checkout')) {
        const Checkout = DynamicComponent({
            loader: () => import('./Components/Checkout'),
        });
        renderReact(
            <Provider store={store}>
                <Checkout />
            </Provider>,
            document.getElementById('checkout')
        );
    }
    if (document.getElementById('lightBoxImages')) {
        const LightboxImages = DynamicComponent({
            loader: () => import('./Components/LightboxImages'),
        });
        import('./Reducers/LightboxImages.reducer').then(({ lightboxImages }) => {
            store.injectReducer('lightboxImages', lightboxImages);
            const rootElement = document.getElementById('lightBoxImages');
            const images = Array.from(rootElement.querySelectorAll('template')).map((img) => ({
                html: img.innerHTML,
                src: img.dataset.src,
            }));
            renderReact(
                <Provider store={store}>
                    <LightboxImages images={images} />
                </Provider>,
                document.getElementById('lightBoxImages')
            );
        });
    }

    if (document.querySelectorAll('.slider').length > 0) {
        const Slider = DynamicComponent({
            loader: () => import('./Components/Slider'),
        });
        Array.from(document.querySelectorAll('.slider')).forEach((slider, index) => {
            const values = [...slider.querySelectorAll('template')].map((block) => {
                return {
                    html: block.innerHTML,
                };
            });
            if (values.length > 0) {
                renderReact(<Slider values={values} />, slider);
            }
        });
    }

    if (document.querySelectorAll('buy-button').length > 0) {
        const BuyButton = DynamicComponent({
            loader: () => import('./Components/BuyButton'),
        });
        Array.from(document.querySelectorAll('buy-button')).forEach((button) => {
            const { articleNumber, quantityFieldId, href, cssClass, label } = button.dataset;
            renderReact(
                <Provider store={store}>
                    <BuyButton
                        {...{
                            label,
                            articleNumber,
                            quantityFieldId,
                            href,
                            cssClass,
                        }}
                    />
                </Provider>,
                button
            );
        });
    }

    if (document.getElementById('myPageMyOpenOrders')) {
        const OrderList = DynamicComponent({
            loader: () => import('./Containers/OrderHistoryList.container'),
        });
        renderReact(
            <Provider store={store}>
                <OrderList />
            </Provider>,
            document.getElementById('myPageMyOpenOrders')
        );
    }

    if (document.querySelectorAll('.js-mui-dropdown').length > 0) {
        const DropDown = DynamicComponent({
            loader: () => import('./Components/Inputs/DropDown'),
        });
        Array.from(document.querySelectorAll('.js-mui-dropdown')).forEach((select) => {
            const values = {
                variant: select.dataset.variant,
                label: select.dataset.label,
                id: select.dataset.id,
                name: select.dataset.name,
                items: select.dataset.items.length > 0 ? JSON.parse(select.dataset.items) : null,
                hasBorder: select.dataset.hasborder.toLowerCase() === 'true' ? true : false,
                icon: select.dataset.icon,
            };
            renderReact(
                <Provider store={store}>
                    <DropDown {...values} />
                </Provider>,
                select
            );
        });
    }

    const googleTagUpdate_selectedOrganization = (orgId, orgName) => {

        if(orgId == null || orgName == null)
            return;
    
        window?.gtag && window.gtag('event', 'selectedOrganization', {
            event_category: 'custom',
            event_label: 'customer_selectedOrganization',
            organization_id: orgId,
            organization_name: orgName
        });
    }


    if (document.querySelectorAll('.js-organization-selector').length > 0) {
        const OrganizationSelector = DynamicComponent({
            loader: () => import('./Containers/OrganizationSelector.container'),
        });
        
        // Check if organization has changed to trigger google tag event
        var orgInfo = window.__litium.preloadState.organizationInfo;
        if(String(orgInfo?.organizationHasChanged).toLowerCase() === 'true')
        {
            googleTagUpdate_selectedOrganization(orgInfo?.organizationId, orgInfo?.organizationName);
            orgInfo.organizationHasChanged = false;
        }

        Array.from(document.querySelectorAll('.js-organization-selector')).forEach((value) => {
            const values = {
                items: value.dataset.items.length > 0 ? JSON.parse(value.dataset.items) : null,
                hasButton: value.dataset.hasbutton.toLowerCase() === 'true' ? true : false,
                variant: value.dataset.variant,
                label: value.dataset.label,
                id: value.dataset.id,
                name: value.dataset.name,
                icon: value.dataset.icon,
                reload: value.dataset.reload.toLowerCase() === 'true' ? true : false,
            };
            renderReact(
                <Provider store={store}>
                    <OrganizationSelector {...values} />
                </Provider>,
                value
            );
        });
    }

    if (document.querySelectorAll('.js-mui-icon').length > 0) {
        const Icon = DynamicComponent({
            loader: () => import('./Components/Icon/Icon'),
        });
        Array.from(document.querySelectorAll('.js-mui-icon')).forEach((value) => {
            const values = {
                icon: value.dataset.icon,
            };
            renderReact(
                <Provider store={store}>
                    <Icon {...values} />
                </Provider>,
                value
            );
        });
    }

    if (document.getElementById('js-tab-selector')) {
        const TabSelector = DynamicComponent({
            loader: () => import('./Components/TabSelector'),
        });
        const logoutLink = document.getElementById('js-tab-selector').dataset.logoutlink;
        const tabsEnabled = document.getElementById('js-tab-selector').dataset.tabsEnabled;

        renderReact(
            <Provider store={store}>
                <TabSelector
                    logoutLink={logoutLink}
                    tabsEnabled={tabsEnabled}
                />
            </Provider>,
            document.getElementById('js-tab-selector')
        );
    }
    
    // Menu collapse toggle
    const collapseBtn = document.getElementById('js-menu-collapse-btn');
    if (collapseBtn) {
        const menuContainer = collapseBtn.closest('.tab-menu-container');
        if (!menuContainer) 
            return;

        const menuItems = menuContainer.querySelectorAll('.tab__header');

        collapseBtn.addEventListener('click', () => {
            const collapsed = menuContainer.classList.toggle('menu-collapsed');
            collapseBtn.title = collapsed ? collapseBtn.dataset.expandTitle : collapseBtn.dataset.collapseTitle;
            menuItems.forEach((item) => {
                // Get text excluding the icon's text content
                const icon = item.querySelector('.js-mui-icon');
                const iconText = icon ? icon.textContent : '';
                const fullText = item.textContent.trim();
                const label = fullText.replace(iconText, '').trim();
                item.title = collapsed ? label : '';
            });
        });
    }

    if (document.getElementById('myPageMyStatements')) {
        const StatementsContainer = DynamicComponent({
            loader: () => import('./Containers/Statements.container'),
        });
        renderReact(
            <Provider store={store}>
                <StatementsContainer />
            </Provider>,
            document.getElementById('myPageMyStatements')
        );
    }

    if (document.getElementById('myPageMyPayments')) {
        const PaymentsContainer = DynamicComponent({
            loader: () => import('./Containers/Payments.container'),
        });
        renderReact(
            <Provider store={store}>
                <PaymentsContainer />
            </Provider>,
            document.getElementById('myPageMyPayments')
        );
    }

    if (document.getElementById('myPagePaymentMethods')) {
        const PaymentMethodsContainer = DynamicComponent({
            loader: () => import('./Containers/PaymentMethods.container'),
        });
        renderReact(
            <Provider store={store}>
                <PaymentMethodsContainer />
            </Provider>,
            document.getElementById('myPagePaymentMethods')
        );
    }

    const myPagePayInvoices = document.getElementById('myPagePayInvoices');
    if (myPagePayInvoices) {
        const PayInvoicesContainer = DynamicComponent({
            loader: () => import('./Containers/PayInvoices.container'),
        });
        const payInvoicesButtonEnabled = myPagePayInvoices.dataset?.payinvoicesbuttonenabled?.toLowerCase() === 'true';

        renderReact(
            <Provider store={store}>
                <PayInvoicesContainer payInvoicesButtonEnabled={payInvoicesButtonEnabled} />
            </Provider>,
            myPagePayInvoices
        );
    }

    const myPageAccountSummary = document.getElementById('myPageAccountSummary');
    if (myPageAccountSummary) {
        const AccountSummaryContainer = DynamicComponent({
            loader: () => import('./Containers/AccountSummary.container'),
        });

        const myPageAccountSummaryBillingInfo = document.getElementById('myPageAccountSummaryBillingInfo');

        renderReact(
            <Provider store={store}>
                <AccountSummaryContainer billingInfoDiv={myPageAccountSummaryBillingInfo} />
            </Provider>,
            myPageAccountSummary
        );
    }

    if (document.getElementById('myPageCustomerTrips')) {
        const CustomerTripContainer = DynamicComponent({
            loader: () => import('./Containers/CustomerTrips.container'),
        });
        renderReact(
            <Provider store={store}>
                <CustomerTripContainer />
            </Provider>,
            document.getElementById('myPageCustomerTrips')
        );
    }

    if (document.getElementById('myPageMyQuotes')) {
        const MyQuotesContainer = DynamicComponent({
            loader: () => import('./Containers/MyQuotes.container'),
        });
        renderReact(
            <Provider store={store}>
                <MyQuotesContainer />
            </Provider>,
            document.getElementById('myPageMyQuotes')
        );
    }

    if (document.getElementById('myPageMyInvoices')) {
        const InvoicesContainer = DynamicComponent({
            loader: () => import('./Containers/Invoices.container'),
        });
        renderReact(
            <Provider store={store}>
                <InvoicesContainer />
            </Provider>,
            document.getElementById('myPageMyInvoices')
        );
    }

    if (document.getElementById('myPageFavoriteLists')) {
        const FavoriteListsContainer = DynamicComponent({
            loader: () => import('./Containers/FavoriteLists.container'),
        });
        renderReact(
            <Provider store={store}>
                <FavoriteListsContainer />
            </Provider>,
            document.getElementById('myPageFavoriteLists')
        );
    }

    if (document.getElementById('buyButtonComponent')) {
        const element = document.getElementById('buyButtonComponent');

        const context = element.dataset.context;
        const productData = JSON.parse(element.dataset.productdata);

        const props = {
            articleNumber: productData?.articleNumber,
            isBuyable: productData?.isBuyable,
            context: context,
            quantityIncrement: productData?.quantityIncrement,
        };

        const CustomBuyButton = DynamicComponent({
            loader: () => import('./Components/customBuyButton'),
        });

        renderReact(
            <Provider store={store}>
                <CustomBuyButton {...props} />
            </Provider>,
            document.getElementById('buyButtonComponent')
        );
    }

    // const favListButton = document.getElementById('favoriteListButton'); // Test variable to skip extra lookups
    if (document.getElementById('favoriteListButton')) {
        const element = document.getElementById('favoriteListButton');

        const context = element.dataset.context;
        const productData = JSON.parse(element.dataset.productdata);

        const props = {
            articleNumber: productData?.articleNumber,
            context: context,
        };

        const FavoriteListButton = DynamicComponent({
            loader: () => import('./Components/FavoriteListButton'),
        });

        renderReact(
            <Provider store={store}>
                <FavoriteListButton {...props} />
            </Provider>,
            document.getElementById('favoriteListButton')
        );
    }

    if (document.querySelectorAll('.js-product-list').length > 0) {
        const ProductList = DynamicComponent({
            loader: () => import('./Components/ProductList'),
        });
        Array.from(document.querySelectorAll('.js-product-list')).forEach((value) => {
            const props = {
                productList: value.dataset.products.length > 0 ? JSON.parse(value.dataset.products) : null,
            };
            renderReact(
                <Provider store={store}>
                    <ProductList {...props} />
                </Provider>,
                value
            );
        });
    }

    if (document.getElementById('js-map')) {
        const MapContainer = DynamicComponent({
            loader: () => import('./Components/Map/MapContainer'),
        });
        const googleMapsApiKey = document.getElementById('js-map').dataset.googlemapsapikey;
        const branchList = document.getElementById('js-map').dataset.branches;
        const productCategories = document.getElementById('js-map').dataset.productCategories;

        renderReact(
            <Provider store={store}>
                <MapContainer
                    branchList={JSON.parse(branchList)}
                    googleMapsApiKey={googleMapsApiKey}
                    productCategories={JSON.parse(productCategories)}
                />
            </Provider>,
            document.getElementById('js-map')
        );
    }

    const mapBranchDetail = document.getElementById('js-map-branch-detail');
    if (mapBranchDetail) {
        const MapBranchDetail = DynamicComponent({
            loader: () => import('./Components/Map/MapBranchDetail'),
        });
        const googleMapsApiKey = mapBranchDetail.dataset.googlemapsapikey;
        const latitude = mapBranchDetail.dataset.latitude;
        const longitude = mapBranchDetail.dataset.longitude;
        const pinUrl = mapBranchDetail.dataset.pinUrl;

        renderReact(
            <Provider store={store}>
                <MapBranchDetail
                    googleMapsApiKey={googleMapsApiKey}
                    latitude={latitude}
                    longitude={longitude}
                    pinUrl={pinUrl}
                />
            </Provider>,
            mapBranchDetail
        );
    }

    if (document.querySelectorAll('.js-accordion-brandlist-block').length > 0) {
        const AccordionBrandList = DynamicComponent({
            loader: () => import('./Components/AccordionBrandList'),
        });
        Array.from(document.querySelectorAll('.js-accordion-brandlist-block')).forEach((block) => {
            const { header, text, items } = block.dataset;
            renderReact(
                <Provider store={store}>
                    <AccordionBrandList
                        header={header}
                        text={text}
                        items={JSON.parse(items)}
                    />
                </Provider>,
                block
            );
        });
    }

    let customerTripPointsTable = document.getElementById('js-customerTripPointsTable');
    if (customerTripPointsTable) {
        const CustomerTripsTable = DynamicComponent({
            loader: () => import('./Components/CustomerTripsTable'),
        });
        const { items } = customerTripPointsTable.dataset;
        renderReact(
            <Fragment>
                <CustomerTripsTable customerTripData={JSON.parse(items)} />
            </Fragment>,
            customerTripPointsTable
        );
    }

    if (document.getElementById('js-sub-category-navigation')) {
        const SubNavCompact = DynamicComponent({
            loader: () => import('./Components/SubNav'),
        });

        const subNavigation = document.getElementById('js-sub-category-navigation').dataset.subCategoryNavigation;

        renderReact(
            <Provider store={store}>
                <SubNavCompact subNavigation={JSON.parse(subNavigation)} />
            </Provider>,
            document.getElementById('js-sub-category-navigation')
        );
    }
};

loaderComponent();
modalResultComponent();
bootstrapComponents();
VanillaComponents();
